import {
  SchedulerSidebarCrewMember,
  SchedulerSidebarEquipmentMember,
} from '@contexts/scheduler/sidebar/types';
import {
  CurrentSchedule,
  RequestScheduleEquipmentAttributes,
  RequestScheduleMembersAttributes,
} from '@services/schedules/internal/types';
import { IsScheduleDataUnchangedValues } from './types';
import { toZonedTime } from 'date-fns-tz';

const getInitialCrewMembers = (
  curr: SchedulerSidebarCrewMember[],
  prev?: SchedulerSidebarCrewMember[],
): RequestScheduleMembersAttributes[] =>
  curr
    .filter((c) => c.userId)
    .map((v) => {
      const prevEl = prev?.find((el) => el.userId === v.userId);
      if (prevEl)
        return { id: prevEl.id, userId: prevEl.userId } as RequestScheduleMembersAttributes;
      return { userId: v.userId } as RequestScheduleMembersAttributes;
    });

const getRemovedCrewMembers = (
  curr: SchedulerSidebarCrewMember[],
  prev?: SchedulerSidebarCrewMember[],
): RequestScheduleMembersAttributes[] => {
  return (
    prev
      ?.filter((el) => {
        return !curr.some((m) => m.userId === el.userId);
      })
      .map((el) => ({ id: el.id || '', userId: el.userId, _destroy: '1' })) || []
  );
};

const getInitialEquipmentMembers = (
  curr: SchedulerSidebarEquipmentMember[],
  prev?: SchedulerSidebarEquipmentMember[],
): RequestScheduleEquipmentAttributes[] =>
  curr
    .filter((c) => c.assetId)
    .map((v) => {
      const prevEl = prev?.find((el) => el.assetId === v.assetId);
      if (prevEl)
        return { id: prevEl.id, assetId: prevEl.assetId } as RequestScheduleEquipmentAttributes;
      return { assetId: v.assetId } as RequestScheduleEquipmentAttributes;
    });

const getRemovedEquipmentMembers = (
  curr: SchedulerSidebarEquipmentMember[],
  prev?: SchedulerSidebarEquipmentMember[],
): RequestScheduleEquipmentAttributes[] => {
  return (
    prev
      ?.filter((el) => {
        return !curr.some((m) => m.assetId === el.assetId);
      })
      .map((el) => ({ id: el.id || '', assetId: el.assetId, _destroy: '1' })) || []
  );
};

export const parseScheduleMembersAttributesToRequest = (
  crewMembers: SchedulerSidebarCrewMember[],
  prev?: SchedulerSidebarCrewMember[],
): RequestScheduleMembersAttributes[] => {
  return [...getInitialCrewMembers(crewMembers, prev), ...getRemovedCrewMembers(crewMembers, prev)];
};

export const parseScheduleEquipmentAttributesToRequest = (
  equipmentMembers: SchedulerSidebarEquipmentMember[],
  prev?: SchedulerSidebarEquipmentMember[],
): RequestScheduleEquipmentAttributes[] => {
  return [
    ...getInitialEquipmentMembers(equipmentMembers, prev),
    ...getRemovedEquipmentMembers(equipmentMembers, prev),
  ];
};

const formatDate = (date: string, zone: string) => {
  return toZonedTime(date, zone).toISOString();
};

export const isScheduleDataUnchanged = (
  values: IsScheduleDataUnchangedValues,
  currentSchedule: CurrentSchedule,
  zone: string,
) => {
  if (!currentSchedule) return false;

  const statusChangedToDraft =
    values.status === 'draft' && values.status !== currentSchedule.status;
  return (
    !statusChangedToDraft &&
    values.startsAt === formatDate(currentSchedule.startsAt, zone) &&
    values.endsAt === formatDate(currentSchedule.endsAt, zone) &&
    values.foremanId === currentSchedule.foreman?.id &&
    values.noteContent === (currentSchedule?.task?.note?.content ?? '')
  );
};
